import React, { useState, useEffect } from 'react';
import { Search, Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoDark from '../assets/logo-dark-mode.png'; 

const Navbar = ({ onOpenAuth, onSearch }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (idName) => {
    
    const id = idName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const element = document.getElementById(id);
    
    if (element) {
      const offset = 100; 
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      isScrolled 
        ? 'py-3 bg-[#050505]/60 backdrop-blur-2xl border-b border-white/5 shadow-2xl' 
        : 'py-8 bg-transparent'
    }`}>
      
      <div 
        className="absolute bottom-0 left-0 h-[1px] bg-gradient-to-r from-blue-500 to-emerald-400 transition-all duration-100"
        style={{ width: `${scrollProgress}%` }}
      />

      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        
        
        <div 
          className="flex items-center gap-2 cursor-pointer group relative z-50" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <div className="absolute -inset-2 bg-blue-500/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <img src={logoDark} alt="Zento" className="h-8 w-auto relative z-10" />
          <span className="text-xl font-black tracking-tighter text-white relative z-10">
            ZENTO<span className="text-blue-500">.</span>
          </span>
        </div>

        
<div className="hidden lg:flex items-center gap-1 bg-white/5 border border-white/5 p-1 rounded-full backdrop-blur-md">
  {['Beneficios', 'Rutas', 'Tecnologias', 'Opiniones'].map((item) => (
    <button 
      key={item}
      onClick={() => scrollToSection(item)}
      
      className="px-5 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-white hover:bg-white/10 rounded-full transition-all"
    >
      {item}
    </button>
  ))}
</div>

        
        <div className="hidden md:flex items-center gap-6">
          
      <div className="relative group flex items-center">
        
        <Search className="absolute left-3 w-4 h-4 text-white/70 group-focus-within:text-blue-500 transition-colors" />
  
        <input 
          type="text"
          placeholder="Buscar curso..."
          
          className="bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-blue-500/50 focus:bg-white/10 transition-all w-40 lg:w-56 placeholder:text-white/40"
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

          <button 
            onClick={onOpenAuth}
            className="group relative px-6 py-2.5 bg-blue-600 rounded-xl overflow-hidden font-black text-xs uppercase tracking-widest transition-all hover:bg-blue-500 active:scale-95"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <span className="relative flex items-center gap-2">
              Empezar <ArrowRight size={14} />
            </span>
          </button>
        </div>

        
        <button className="md:hidden text-white z-50" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
  {mobileMenuOpen && (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      
      className="fixed inset-0 bg-[#050505] z-[999] flex flex-col md:hidden w-screen h-screen"
    >
      
      <div className="flex items-center justify-between px-6 py-8 border-b border-white/5">
        <div className="flex items-center gap-2">
          <img src={logoDark} alt="Zento" className="h-8 w-auto" />
          <span className="text-xl font-black text-white">ZENTO.</span>
        </div>
        <button 
          onClick={() => setMobileMenuOpen(false)} 
          className="text-white p-2 hover:bg-white/5 rounded-full"
        >
          <X size={32} />
        </button>
      </div>

      
      <div className="flex flex-col items-center justify-center flex-1 gap-10 px-6">
        {['Beneficios', 'Rutas', 'Tecnologias', 'Opiniones'].map((item, i) => (
          <motion.button 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            key={item}
            className="text-5xl font-black text-white hover:text-blue-500 uppercase tracking-tighter"
            onClick={() => {
              scrollToSection(item);
              setMobileMenuOpen(false); 
            }}
          >
            {item}
          </motion.button>
        ))}
        
        <button 
          onClick={() => { setMobileMenuOpen(false); onOpenAuth(); }}
          className="w-full max-w-xs py-5 bg-blue-600 text-white rounded-2xl font-black text-xl uppercase tracking-widest mt-4 shadow-2xl shadow-blue-600/30"
        >
          Empezar Ahora
        </button>
      </div>
    </motion.div>
  )}
</AnimatePresence>
    </nav>
  );
};

export default Navbar;